/**
 * ISO-3166 alpha-2 country helpers used for geographic differentiation of listings.
 * Spanish names, because the site is strictly localized to Spanish.
 */

export const COUNTRY_NAMES_ES: Record<string, string> = {
  ES: "España",
  MX: "México",
  PT: "Portugal",
  AD: "Andorra",
  FR: "Francia",
  GB: "Reino Unido",
  US: "Estados Unidos",
  NL: "Países Bajos",
  DE: "Alemania",
  IT: "Italia",
  CO: "Colombia",
  VE: "Venezuela",
  PE: "Perú",
  CL: "Chile",
  AR: "Argentina",
  EC: "Ecuador",
  PA: "Panamá",
  SR: "Surinam",
  TT: "Trinidad y Tobago",
  QA: "Catar",
  MV: "Maldivas",
  IR: "Irán",
  AW: "Aruba",
  BR: "Brasil",
  UY: "Uruguay",
  PY: "Paraguay",
  BO: "Bolivia",
  CR: "Costa Rica",
  GT: "Guatemala",
  DO: "República Dominicana",
  CU: "Cuba",
  BE: "Bélgica",
  CH: "Suiza",
  IE: "Irlanda",
  MA: "Marruecos",
};

/** Slugs used in public URLs and sitemap file names (lowercase ISO code). */
export const countrySlug = (code?: string | null): string =>
  (code || "").trim().toLowerCase();

export const isValidCountryCode = (code?: string | null): boolean =>
  !!code && /^[A-Za-z]{2}$/.test(code.trim());

/** Spanish country name for an ISO code; falls back to the code itself. */
export const countryName = (code?: string | null): string => {
  if (!isValidCountryCode(code)) return "";
  const upper = code!.trim().toUpperCase();
  return COUNTRY_NAMES_ES[upper] || upper;
};

/** Uppercase ISO code, or undefined when the value is not a valid code. */
export const normalizeCountryCode = (code?: string | null): string | undefined =>
  isValidCountryCode(code) ? code!.trim().toUpperCase() : undefined;

/** Country code → Spanish demonym-ish qualifier used in copy, e.g. "en México". */
export const countryInPhrase = (code?: string | null): string => {
  const name = countryName(code);
  return name ? `en ${name}` : "";
};

/** The site's home market. Titles for it stay unchanged to protect existing rankings. */
export const HOME_COUNTRY = "ES";

export const isHomeCountry = (code?: string | null): boolean =>
  normalizeCountryCode(code) === HOME_COUNTRY;

/** Sort helper: home country first, then alphabetical by Spanish name. */
export const sortCountryCodes = (codes: string[]): string[] =>
  [...codes].sort((a, b) => {
    if (a === HOME_COUNTRY) return -1;
    if (b === HOME_COUNTRY) return 1;
    return countryName(a).localeCompare(countryName(b), "es");
  });
